import { StateAdapter } from '@varos/client-base';
import { ReportingRouterStateValues } from '../route/document/documentRouteModel';
import { useState } from 'react';
import { computed, makeObservable } from 'mobx';
import { action } from 'mobx';
import { observable } from 'mobx';
import { EditorSelection } from '@varos/editor-core';
import { observer } from 'mobx-react';
import { ContentObject, SuggestionCreatePayload } from '@varos/research-sdk-api';
import { ReportingShellStateValues } from './base';
import { ComposerMode } from './page';
import useLocalStorageState from 'use-local-storage-state';
import { LocalStorageKeys } from '../../../config';
import { AssistantRuntimeMode, Message } from '@varos/assistant-sdk';

const WORKSPACE_STORAGE_KEY = 'varos_workspace_state';

export type StateDocumentFormDao = {
    tab: 'interview' | 'responses';
};

export type StateComposerDao = {
    mode: ComposerMode;
    text: string;
    isSubmitting: boolean;
    test: boolean;
    version: 'active' | 'latest' | 'preview' | 'develop';
};

type StateDao = {
    overlay: {
        isOpen: boolean;
        page:
        | {
            kind: 'chat';
            width: number | null;
        }
        | {
            kind: 'insert';
        };
    };
    chat: {
        isOpen: boolean;
        thread: string | null;
        assistantMode: AssistantRuntimeMode;
        state: 'preparing' | 'active';
        enqueuedMessages: Array<Pick<Message, 'context' | 'parts'> & { idempotencyKey: string }> | null;
    };
    composer: StateComposerDao;
    documentForm: StateDocumentFormDao;
    sidebarWidth: null | number;
    chatWidth: null | number;
    current: Pick<ContentObject, 'id'> | null;
    expanded: string[];
};

class WorkspaceStore {
    _chat: StateDao['chat'] = {
        isOpen: false,
        thread: null,
        assistantMode: 'workspace',
        state: 'active',
        enqueuedMessages: null,
    };
    _overlay: StateDao['overlay'] = {
        isOpen: false,
        page: {
            kind: 'chat',
            width: null,
        },
    };
    _composer: StateComposerDao = {
        mode: 'automatic',
        text: '',
        isSubmitting: false,
        test: process.env.NODE_ENV === 'development',
        version: 'active',
    };
    _documentForm: StateDocumentFormDao = {
        tab: 'interview',
    };
    _expanded: Set<string> = new Set();
    _sidebarWidth: null | number = null;
    _chatWidth: null | number = null;
    _current: Pick<ContentObject, 'id'> | null = null;

    constructor() {
        makeObservable(this, {
            _chat: observable,
            setChat: action,
            chat: computed,
            _composer: observable,
            setComposer: action,
            composer: computed,
            _documentForm: observable,
            setDocumentForm: action,
            documentForm: computed,
            _sidebarWidth: observable,
            setSidebarWidth: action,
            sidebarWidth: computed,
            _chatWidth: observable,
            setChatWidth: action,
            chatWidth: computed,
            _current: observable,
            setCurrent: action,
            current: computed,
            _expanded: observable,
            setExpanded: action,
            expanded: computed,
            _overlay: observable,
            setOverlay: action,
            overlay: computed,
        });

        // Load initial state from session storage
        this.loadFromSessionStorage();
    }

    private loadFromSessionStorage() {
        try {
            const stored = sessionStorage.getItem(WORKSPACE_STORAGE_KEY);
            if (stored) {
                const parsedState: StateDao = JSON.parse(stored);
                // TODO make type-safe
                this._chat = parsedState.chat;
                if (!this._chat.assistantMode) {
                    this._chat.assistantMode = 'workspace';
                }
                this._composer = parsedState.composer;
                this._documentForm = parsedState.documentForm;
                this._sidebarWidth = parsedState.sidebarWidth;
                this._chatWidth = parsedState.chatWidth;
                this._current = parsedState.current;
                this._expanded = new Set(parsedState.expanded);
                this._overlay = parsedState.overlay;
            }
        } catch (error) {
            console.warn('Failed to load workspace state from session storage:', error);
        }
    }

    private saveToSessionStorage() {
        try {
            const stateToSave: StateDao = {
                chat: this._chat,
                composer: this._composer,
                sidebarWidth: this._sidebarWidth,
                documentForm: this._documentForm,
                chatWidth: this._chatWidth,
                current: this._current,
                expanded: Array.from(this._expanded),
                overlay: this._overlay,
            };
            sessionStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (error) {
            console.warn('Failed to save workspace state to session storage:', error);
        }
    }

    // Chat

    setChat(newChat: Partial<StateDao['chat']>) {
        this._chat = {
            ...this._chat,
            ...newChat,
        };
        this.saveToSessionStorage();
    }

    get chat() {
        return this._chat;
    }

    // Composer

    setComposer(newComposer: Partial<StateComposerDao>) {
        this._composer = {
            ...this._composer,
            ...newComposer,
        };
        this.saveToSessionStorage();
    }

    get composer() {
        return this._composer;
    }

    // Document Form

    setDocumentForm(newDocumentForm: Partial<StateDocumentFormDao>) {
        this._documentForm = {
            ...this._documentForm,
            ...newDocumentForm,
        };
        this.saveToSessionStorage();
    }

    get documentForm() {
        return this._documentForm;
    }

    // Overlay

    setOverlay(newOverlay: Partial<StateDao['overlay']>) {
        this._overlay = {
            ...this._overlay,
            ...newOverlay,
        };
        this.saveToSessionStorage();
    }

    get overlay() {
        return this._overlay;
    }

    // Expanded

    setExpanded(newExpanded: Set<string>) {
        this._expanded = newExpanded;
        this.saveToSessionStorage();
    }

    get expanded(): Set<string> {
        return this._expanded;
    }

    // Sidebar

    setSidebarWidth(newSidebarWidth: null | number) {
        this._sidebarWidth = newSidebarWidth;
        this.saveToSessionStorage();
    }

    get sidebarWidth(): null | number {
        return this._sidebarWidth;
    }

    // Chat Width

    setChatWidth(newChatWidth: null | number) {
        this._chatWidth = newChatWidth;
        this.saveToSessionStorage();
    }

    get chatWidth(): null | number {
        return this._chatWidth;
    }

    // Current

    setCurrent(newCurrent: Pick<ContentObject, 'id'> | null) {
        this._current = newCurrent;
        this.saveToSessionStorage();
    }

    get current(): Pick<ContentObject, 'id'> | null {
        return this._current;
    }
}

// MobX store to manage editor state
class EditorStore {
    _selection: EditorSelection | null = null;

    constructor() {
        makeObservable(this, {
            _selection: observable,
            setSelection: action,
            selection: computed,
        });
    }

    setSelection(newSelection: EditorSelection | null) {
        this._selection = newSelection;
    }

    get selection(): EditorSelection | null {
        return this._selection;
    }
}

export function createResearchShellStateProvider(): StateAdapter<ReportingShellStateValues> {
    const store = {
        editor: new EditorStore(),
        workspace: new WorkspaceStore(),
    };
    return {
        insert: {
            useState(context) {
                const [isOpen, setIsOpen] = useLocalStorageState(
                    LocalStorageKeys.INSERT_OVERLAY_STATE,
                    {
                        defaultValue: false,
                    }
                );
                return {
                    value: {
                        overlay: {
                            isOpen,
                        },
                    },
                    onChange(value) {
                        if (value.overlay?.isOpen !== undefined) {
                            setIsOpen(value.overlay.isOpen);
                        }
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return Component;
            },
        },
        chat: {
            useState(context) {
                return {
                    value: {
                        isOpen: store.workspace.chat.isOpen,
                        thread: store.workspace.chat.thread,
                        assistantMode: store.workspace.chat.assistantMode,
                        state: store.workspace.chat.state,
                        enqueuedMessages: store.workspace.chat.enqueuedMessages,
                    },
                    onChange(value) {
                        store.workspace.setChat(value);
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
        composer: {
            useState(context) {
                return {
                    value: {
                        mode: store.workspace.composer.mode,
                        text: store.workspace.composer.text,
                        isSubmitting: store.workspace.composer.isSubmitting,
                        test: store.workspace.composer.test,
                        version: store.workspace.composer.version,
                    },
                    onChange(value) {
                        store.workspace.setComposer(value);
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
        form: {
            useState(context) {
                return {
                    value: {
                        tab: store.workspace.documentForm.tab,
                    },
                    onChange(value) {
                        store.workspace.setDocumentForm(value);
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
        editor: {
            useState(context) {
                return {
                    value: {
                        document: {
                            selection: store.editor.selection,
                        },
                        workspace: {
                            current: store.workspace.current,
                        },
                    },
                    onChange(value) {
                        if (value.document) {
                            store.editor.setSelection(value.document.selection);
                        }
                        if (value.workspace) {
                            store.workspace.setCurrent(value.workspace.current);
                        }
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
        workspace: {
            useState(context) {
                return {
                    value: {
                        current: store.workspace.current,
                    },
                    onChange(value) {
                        if (value.current) {
                            store.workspace.setCurrent(value.current);
                        }
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
        layout: {
            useState(context) {
                return {
                    value: {
                        sidebarWidth: store.workspace.sidebarWidth,
                        chatWidth: store.workspace.chatWidth,
                        expanded: store.workspace.expanded,
                    },
                    onChange(value) {
                        if (value.sidebarWidth) {
                            store.workspace.setSidebarWidth(value.sidebarWidth);
                        }
                        if (value.chatWidth) {
                            store.workspace.setChatWidth(value.chatWidth);
                        }
                        if (value.expanded) {
                            store.workspace.setExpanded(value.expanded);
                        }
                    },
                };
            },
            subscribe<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
                return observer(Component);
            },
        },
    };
}
